<template>
  <v-card
    v-bind:class="{ archived: section.archived, hidden: (!viewArchived && section.archived) }"
    class="section"
  >
    <Preloader v-if="loading" />
    <v-toolbar
      dense
      flat
      color="rgba(0,0,0,0.09)"
      style="position:relative;"
    >
      <div>
        <a
          roll="button"
          class="handle"
          style="float:left;"
        >
          <v-icon small left>drag_handle</v-icon>
        </a>
        <strong
          v-if="section.number"
          v-html="section.number + '.'"
        ></strong>
        &nbsp;
        <span
          class="inlineLimiter"
          v-html="section.text"
          style="margin-right: 50px;"
        ></span>
        <v-chip 
          outline
          color="grey"
          small
          v-if="conditional_part"  
        >
          Conditional
        </v-chip>
        <v-chip 
          outline
          color="grey"
          small
          v-if="section.read_only"  
        >
          Read Only
        </v-chip>
        <v-chip 
          outline
          color="grey"
          small
          v-if="section.web_only"  
        >
          Web Only
        </v-chip>
        <v-chip 
          outline
          color="grey"
          small
          v-if="section.pdf_only"  
        >
          PDF Only
        </v-chip>
        <v-chip 
          outline
          color="grey"
          small
          v-if="section.web_collapsible"  
        >
          Collapsible
        </v-chip>
        <ArchiveButton
          style="float:right;position:absolute;right:45px;top:12px;"
          :archived="section.archived"
          @restore="restore()"
          @archive="archive()"
        />
        <a
          roll="button"
          class="section-edit-button"
          style="float:right;position:absolute;right:10px;top:12px;"
          @click="hidden=!hidden"
        >
          <v-icon
            color="grey"
          >
            {{ hidden ? 'arrow_drop_down' : 'arrow_drop_up'}}
          </v-icon>
        </a>
      </div>
    </v-toolbar>
    <v-expand-transition>
      <div
        v-if="section.id"
        v-show="!hidden"
        style="padding:15px;"
        :data-section-id="section.id"
      >
        <Draggable
          v-model="section.parts"
          :group="{ name: 'form-parts', put: canAcceptPart }"
          handle=".handle"
          draggable=".draggable-item"
          :animation="150"
          easing="cubic-bezier(1, 0, 0, 1)"
          :move="drag"
          @end="endDrag"
          @add="$emit('add', $event)"
        >
          <FormTemplatePart
            v-for="(partId, index) in section.parts"
            :key="typeof partId === 'object' ? 'temp-' + index : partId"
            :partId="partId"
            class="draggable-item"
            @add="$emit('add', $event)"
          />
          <div slot="footer" class="drop-placeholder" v-show="visiblePartsCount === 0">
            <v-icon color="grey lighten-1">add_circle_outline</v-icon>
            <span class="ml-2">Drop component here</span>
          </div>
        </Draggable>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import Draggable from 'vuedraggable'
import FormTemplatePart from './FormTemplatePart'
import Preloader from '@/components/global/Preloader'
import ArchiveButton from './ArchiveButton'

export default {
  name: 'FormTemplateSection',
  props: [ 'partId', 'newPart' ],
  components: {
    Draggable,
    Preloader,
    ArchiveButton
  },
  beforeCreate () {
    this.$options.components.FormTemplatePart = FormTemplatePart
  },
  data () {
    return {
      adding: null,
      loading: false,
      hidden: false,
      draggedElement: null,
      conditional_part: false
    }
  },
  created () {
    this.conditional_part = (this.section.conditional_question_id)
  },
  computed: {
    ...mapGetters('formtemplate', [
      'partById'
    ]),
    ...mapState('formtemplate', [
      'viewArchived'
    ]),
    isHtmlTitle () {
      var htmlTag = /<[a-z][\s\S]*>/i
      return htmlTag.test(this.section.text)
    },
    section: function () {
      return this.partById(this.partId) || this.newPart
    },
    draggableClassName: function () {
      return 'draggable-item'
    },
    visiblePartsCount () {
      if (!this.section || !this.section.parts) return 0
      return this.section.parts.filter(partId => {
        const part = this.partById(partId)
        return part && (!part.archived || this.viewArchived)
      }).length
    }
  },
  methods: {
    ...mapActions('formtemplate', [
      'saveSection',
      'moveSection',
      'moveQuestion',
      'moveParagraph',
      'movePagebreak',
      'moveEsignature'
    ]),
    archive () {
      this.loading = true
      this.section.archived = true
      this.saveSection(this.section)
      .then(section => {
        this.loading = false
        this.$emit('save', section)
      })
    },
    restore () {
      this.loading = true
      this.section.archived = false
      this.saveSection(this.section)
      .then(section => {
        this.loading = false
        this.$emit('save', section)
      })
    },
    createNewPart () {
      let newPart = {
        template: this.section.template,
        section: this.section.id
      }
      switch (this.adding) {
        case 'section':
          newPart.part_type = 's'
          break
        case 'paragraph':
          newPart.part_type = 'p'
          break
        case 'pagebreak':
          newPart.part_type = 'b'
          break
        case 'question':
          newPart.part_type = 'q'
          newPart.is_list = false
          newPart.default_rows = 1
          newPart.fieldtype = {}
          break
        case 'esignature':
          newPart.part_type = 'e'
          break
      }
      return newPart
    },
    partAdded (part) {
      this.adding = null
      this.section.parts.push(part.id)
    },
    canAcceptPart () {
      return true
    },
    drag(e) {
      this.draggedElement = e.draggedContext.element
    },
    endDrag (event) {
      let movedPart = this.partById(this.draggedElement)
      this.draggedElement = null
      if (!movedPart) return
      
      const targetEl = event.to
      const sectionEl = targetEl.closest('[data-section-id]')
      const targetSectionId = sectionEl ? sectionEl.dataset.sectionId : undefined
      const toSection = (!targetSectionId || targetSectionId === 'root')
        ? null
        : parseInt(targetSectionId)

      let insert_before = null
      const targetList = toSection ? this.partById(toSection).parts : this.$store.state.formtemplate.template.root_parts
      if (targetList && targetList.length > event.newIndex + 1) {
          const nextItem = targetList[event.newIndex + 1]
          insert_before = typeof nextItem === 'object' ? nextItem.id : nextItem || null
      }

      if (movedPart.part_type === 's') {
        this.moveSection({ section: movedPart, insert_before, toSection })
      } else if (movedPart.part_type === 'q') {
        this.moveQuestion({ question: movedPart, insert_before, toSection })
      } else if (movedPart.part_type === 'p') {
        this.moveParagraph({ paragraph: movedPart, insert_before, toSection })
      } else if (movedPart.part_type === 'b') {
        this.movePagebreak({ pagebreak: movedPart, insert_before, toSection })
      } else if (movedPart.part_type === 'e') {
        this.moveEsignature({ esignature: movedPart, insert_before, toSection })
      } else {
        throw new TypeError('Unsupported part type')
      }
    }
  }
}
</script>

<style scoped>
.section-edit-button {
  margin-left: 10px;
}

.section {
  margin-bottom: 20px;
  border:1px rgba(0,0,0,0.1) solid;
}

.inlineLimiter >>> :first-child {
  display:inline;
}

.hidden {
  display: none !important;
}

.drop-placeholder {
  margin-top: 10px;
  padding: 20px;
  border: 2px dashed rgba(0,0,0,0.15);
  border-radius: 4px;
  background-color: rgba(0,0,0,0.02);
  color: rgba(0,0,0,0.4);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}
</style>
